// Shared helpers for the events country filter. Event `country` values are
// stored as ISO-3166 alpha-2 codes (e.g. "US", "IN"), matching the geolocation
// lookup used on the homepage, so the visitor's country can be matched directly.

let regionNames
try {
  regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
} catch (e) {
  regionNames = null
}

// Turns an ISO-2 country code into a human-readable name, falling back to the
// raw value when the platform can't resolve it (e.g. an unexpected full name).
export const regionLabel = (code, region = '') => {
  if (!code) return ''
  const upper = String(code).trim().toUpperCase()
  if (regionNames && upper.length === 2) {
    let country = ''
    try {
      country = regionNames.of(upper) || code
    } catch (e) {
      country = code
    }
    return region ? `${region}, ${country}` : country
  }
  return code
}

// Case-insensitive match between a stored country value and the geolocation
// country code so the filter can default to the visitor's country.
const US_COUNTRY_ALIASES = new Set([
  'us',
  'usa',
  'united states',
  'united states of america'
])

const normalizeCountryToken = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

export const matchesUserCountry = (storedCountry, userCode) => {
  if (!storedCountry || !userCode) return false
  const stored = normalizeCountryToken(storedCountry)
  const user = normalizeCountryToken(userCode)
  if (stored === user) return true
  if (US_COUNTRY_ALIASES.has(stored) && US_COUNTRY_ALIASES.has(user)) {
    return true
  }
  return false
}

export const regionSelectionKey = (entry) => {
  if (!entry) return 'all'
  return `${String(entry.country || '')
    .trim()
    .toUpperCase()}|${String(entry.region || '')
    .trim()
    .toLowerCase()}`
}

export const isSameRegionSelection = (a, b) =>
  regionSelectionKey(a) === regionSelectionKey(b)

export const countryDisplayLabel = (country) => {
  if (!country) return 'Other'
  const trimmed = String(country).trim()
  if (trimmed.length === 2 && regionNames) {
    try {
      return regionNames.of(trimmed.toUpperCase()) || trimmed
    } catch (e) {
      return trimmed
    }
  }
  return trimmed
}

export const countryGroupKey = (country) =>
  normalizeCountryToken(country) || 'other'

export const groupRegionsByCountry = (regions, visitorCountryCode) => {
  const groups = new Map()

  regions.forEach((entry) => {
    const raw = entry.country || ''
    const key = countryGroupKey(raw)
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        countryRaw: raw,
        countryLabel: countryDisplayLabel(raw),
        regions: []
      })
    }
    groups.get(key).regions.push(entry)
  })

  const list = Array.from(groups.values()).map((group) => ({
    ...group,
    regions: group.regions.sort((a, b) =>
      String(a.region).localeCompare(String(b.region), undefined, {
        sensitivity: 'base'
      })
    )
  }))

  list.sort((a, b) => {
    const aHome =
      visitorCountryCode && matchesUserCountry(a.countryRaw, visitorCountryCode)
    const bHome =
      visitorCountryCode && matchesUserCountry(b.countryRaw, visitorCountryCode)
    if (aHome && !bHome) return -1
    if (bHome && !aHome) return 1
    return a.countryLabel.localeCompare(b.countryLabel, undefined, {
      sensitivity: 'base'
    })
  })

  return list
}

export const stateDisplayLabel = (entry) => {
  if (!entry?.region) return ''
  return String(entry.region).trim()
}

export const regionFilterLabel = (entry) => {
  if (!entry?.region) return ''
  const region = String(entry.region).trim()
  if (!entry.country) return region
  const country = String(entry.country).trim()
  if (country.length === 2) return regionLabel(country, region)
  return `${region}, ${country}`
}

export const matchesUserRegion = (storedRegion, userRegion) => {
  if (!storedRegion || !userRegion) return false
  return (
    String(storedRegion).trim().toLowerCase() ===
    String(userRegion).trim().toLowerCase()
  )
}
